import {getTestProps} from '@benchling/js-test-utils';
import {Loader, Text} from '@mantine/core';
import React, {ChangeEvent, useCallback, useRef, useState} from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import DropTarget from 'util/drag-and-drop/DropTarget';
import {getUserMessage} from 'util/Net';

type UploadState = 'idle' | 'uploading' | 'error';

interface UploadSkillModalProps {
  onRequestClose: () => void;
  onRequestSubmit?: (file: File) => void | Promise<void>;
}

function isSkillFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return name.endsWith('.zip') || name.endsWith('.skill');
}

export default function UploadSkillModal({
  onRequestClose,
  onRequestSubmit,
}: UploadSkillModalProps): JSX.Element {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSelectFile = useCallback((file: File) => {
    if (!isSkillFile(file)) {
      setErrorMessage('Please select a .zip or .skill file containing a SKILL.md.');
      return;
    }
    setErrorMessage(null);
    setSelectedFile(file);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      const file = e.dataTransfer?.files[0];
      if (file) {
        handleSelectFile(file);
      }
    },
    [handleSelectFile]
  );

  const handleFileInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      const file = e.target.files?.[0];
      if (file) {
        handleSelectFile(file);
      }
      e.target.value = '';
    },
    [handleSelectFile]
  );

  async function handleSubmit(): Promise<void> {
    if (selectedFile == null || uploadState === 'uploading') {
      return;
    }
    setUploadState('uploading');
    setErrorMessage(null);
    try {
      if (onRequestSubmit) {
        await onRequestSubmit(selectedFile);
      }
      onRequestClose();
    } catch (err: unknown) {
      setUploadState('error');
      const serverMessage = getUserMessage(err);
      if (serverMessage) {
        setErrorMessage(serverMessage);
      } else if (err instanceof Error) {
        setErrorMessage(err.message);
      } else {
        throw err;
      }
    }
  }

  const buttons: Array<ModalButton> = [
    {text: 'Cancel', onClick: onRequestClose},
    {
      text: 'Upload Skill',
      submit: true,
      style: 'primary',
      disabled: selectedFile == null || uploadState === 'uploading',
    },
  ];

  return (
    <Modal
      title="Upload Skill"
      buttons={buttons}
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSubmit}
    >
      <div style={{display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0'}}>
        {errorMessage != null && (
          <div
            style={{
              padding: '10px 12px',
              borderRadius: 4,
              border: '1px solid #e03131',
              backgroundColor: '#fff5f5',
              color: '#c92a2a',
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            <Text fw="bold" size="sm" style={{color: '#c92a2a', marginBottom: 4}}>
              Error
            </Text>
            {errorMessage}
          </div>
        )}

        <div>
          <Text size="sm" c="dimmed" style={{marginBottom: 8}}>
            Upload a Skill archive (.zip or .skill) containing a SKILL.md file with YAML frontmatter.
          </Text>

          <DropTarget
            shouldCaptureDrop={true}
            mimeTypes={['Files']}
            dropEffect="copy"
            targetDesc=".skill or .zip file"
            onDrop={handleDrop}
          >
            <div
              role="button"
              tabIndex={0}
              style={{
                minHeight: 80,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px dashed #ccc',
                borderRadius: 8,
                cursor: 'pointer',
                padding: '16px',
              }}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
            >
              {uploadState === 'uploading' ? (
                <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                  <Loader size="sm" />
                  <Text size="sm" c="dimmed">
                    Uploading skill...
                  </Text>
                </div>
              ) : selectedFile != null ? (
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4}}>
                  <BenchlingIcon icon="file" style={{fontSize: 24, color: '#666'}} />
                  <Text size="sm" fw="bold">
                    {selectedFile.name}
                  </Text>
                  <Text size="xs" c="dimmed">
                    Click or drop to replace
                  </Text>
                </div>
              ) : (
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4}}>
                  <BenchlingIcon icon="upload" style={{fontSize: 24, color: '#999'}} />
                  <Text size="sm" c="dimmed">
                    Drop a .zip or .skill file here, or click to select
                  </Text>
                </div>
              )}
            </div>
          </DropTarget>
          <input
            {...getTestProps({component: 'UploadSkillModal', element: 'fileInput'})}
            type="file"
            accept=".zip,.skill"
            className="hidden"
            ref={fileInputRef}
            onChange={handleFileInputChange}
          />
        </div>
      </div>
    </Modal>
  );
}
